import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatCard } from './StatCard';
import { MessageSquare, Home, Building2, Tag, LandPlot, Store, Eye, BadgeCheck, ExternalLink, Inbox, FolderKanban, Users, BarChart3, Star, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function AgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions: perms } = useDashboardPermissions();

  // Resolve agency context
  const { data: agency } = useQuery({
    queryKey: ['agent-agency', user?.id],
    queryFn: async () => {
      const { data: ap } = await supabase
        .from('agent_profiles').select('agency_id').eq('user_id', user!.id).maybeSingle();
      if (!ap?.agency_id) return null;
      const { data: ag } = await supabase
        .from('agencies').select('id, name, slug, is_verified, logo_url, city')
        .eq('id', ap.agency_id).maybeSingle();
      const { data: dev } = await supabase
        .from('developer_profiles').select('id').eq('user_id', (await supabase.from('agencies').select('created_by').eq('id', ap.agency_id).maybeSingle()).data?.created_by).maybeSingle();
      return { ...ag, hasDeveloperProfile: !!dev };
    },
    enabled: !!user,
  });

  const { data: stats, isLoading } = useQuery({
    queryKey: ['agent-dashboard-stats-v2', user?.id],
    queryFn: async () => {
      const [{ count: totalListings }, { count: activeListings }, { count: myLeads }, { count: assignedLeads }] = await Promise.all([
        supabase.from('properties').select('*', { count: 'exact', head: true }).eq('user_id', user!.id),
        supabase.from('properties').select('*', { count: 'exact', head: true }).eq('user_id', user!.id).eq('status', 'active'),
        supabase.from('leads').select('*', { count: 'exact', head: true }).eq('agent_id', user!.id),
        supabase.from('leads').select('*', { count: 'exact', head: true }).eq('assigned_to', user!.id).neq('agent_id', user!.id),
      ]);
      return {
        totalListings: totalListings || 0,
        activeListings: activeListings || 0,
        myLeads: myLeads || 0,
        assignedLeads: assignedLeads || 0,
      };
    },
    enabled: !!user,
  });

  // Agency settings (for "first to claim" CTA)
  const { data: agencySettings } = useQuery({
    queryKey: ['agent-agency-settings', agency?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agency_settings')
        .select('lead_assignment_mode')
        .eq('agency_id', agency!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!agency?.id,
  });

  // Agency-wide listings (read-only summary)
  const { data: agencyListings } = useQuery({
    queryKey: ['agent-agency-listings', agency?.id],
    queryFn: async () => {
      const { data: team } = await supabase
        .from('agent_profiles').select('user_id').eq('agency_id', agency!.id);
      const ids = (team || []).map(t => t.user_id);
      if (!ids.length) return [];
      const { data } = await supabase
        .from('properties')
        .select('id, title, city, price, currency, status, user_id, view_count')
        .in('user_id', ids)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!agency?.id,
  });

  // Two distinct lead sets
  const { data: myLeadsList } = useQuery({
    queryKey: ['agent-my-leads', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, status, created_at')
        .eq('agent_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  const { data: assignedLeadsList } = useQuery({
    queryKey: ['agent-assigned-leads', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, status, created_at, claimed_at')
        .eq('assigned_to', user!.id)
        .neq('agent_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  const statusColors: Record<string, string> = {
    new: 'bg-blue-500', contacted: 'bg-yellow-500', converted: 'bg-green-500', closed: 'bg-gray-400',
  };

  if (isLoading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Agent Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your listings, track leads, and monitor performance.</p>
      </div>

      {/* Agency strip */}
      {agency && (
        <Card className="rounded-2xl border-0 shadow-sm bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-background flex items-center justify-center shrink-0 overflow-hidden">
              {agency.logo_url ? <img src={agency.logo_url} alt={agency.name} className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-primary" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold truncate">{agency.name}</span>
                {agency.is_verified && <BadgeCheck className="h-4 w-4 text-primary" />}
              </div>
              <div className="text-xs text-muted-foreground">Member of {agency.city || 'this agency'}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/agencies/${agency.slug || agency.id}`)}>
              <ExternalLink className="h-3.5 w-3.5 mr-1" />View Profile
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quick actions — gated by permission flags */}
      {(perms.canAddProperty || perms.canAddPlot || perms.canAddCommercial || perms.canAddProject || perms.reasons.addProperty || perms.reasons.addProject) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(perms.canAddProperty || perms.reasons.addProperty) && (
            <QuickAction
              icon={Tag} label="Add Property"
              enabled={perms.canAddProperty}
              reason={perms.reasons.addProperty}
              onClick={() => navigate('/dashboard/properties/new')}
            />
          )}
          {(perms.canAddPlot || perms.reasons.addPlot) && (
            <QuickAction
              icon={LandPlot} label="Add Plot"
              enabled={perms.canAddPlot}
              reason={perms.reasons.addPlot}
              onClick={() => navigate('/dashboard/plots/new')}
            />
          )}
          {(perms.canAddCommercial || perms.reasons.addCommercial) && (
            <QuickAction
              icon={Store} label="Add Commercial"
              enabled={perms.canAddCommercial}
              reason={perms.reasons.addCommercial}
              onClick={() => navigate('/dashboard/commercial/new')}
            />
          )}
          {(perms.canAddProject || perms.reasons.addProject) && (
            <QuickAction
              icon={Building2} label="Add Project"
              enabled={perms.canAddProject}
              reason={perms.reasons.addProject}
              onClick={() => navigate('/dashboard/projects/new')}
            />
          )}
        </div>
      )}

      {/* Pipeline & Performance quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {perms.canViewLeads && (
          <QuickAction icon={Inbox} label="My Leads" enabled onClick={() => navigate('/dashboard/leads')} />
        )}
        {perms.canViewLeadPipeline && (
          <QuickAction icon={FolderKanban} label="Lead Pipeline" enabled onClick={() => navigate('/dashboard/pipeline')} />
        )}
        <QuickAction
          icon={Users} label="Clients"
          enabled={perms.canManageClients}
          reason={perms.reasons.manageClients}
          onClick={() => navigate('/dashboard/clients')}
        />
        <QuickAction
          icon={BarChart3} label="Analytics"
          enabled={perms.canViewAnalytics}
          reason={perms.reasons.viewAnalytics}
          onClick={() => navigate('/dashboard/analytics')}
        />
        {perms.canViewReviews && (
          <QuickAction icon={Star} label="Reviews" enabled onClick={() => navigate('/dashboard/reviews')} />
        )}
        {perms.canViewMyListings && (
          <QuickAction icon={Sparkles} label="Boost Listing" enabled onClick={() => navigate('/dashboard/listings')} />
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard value={stats?.totalListings || 0} label="My Listings" variant="sale" progress={Math.min((stats?.totalListings || 0) * 10, 100)} />
        <StatCard value={stats?.activeListings || 0} label="Active Listings" variant="rent" progress={stats?.totalListings ? Math.round(((stats?.activeListings || 0) / stats.totalListings) * 100) : 0} />
        <StatCard value={stats?.myLeads || 0} label="My Leads" variant="customer" progress={Math.min((stats?.myLeads || 0) * 5, 100)} />
        <StatCard value={stats?.assignedLeads || 0} label="Assigned to Me" variant="city" progress={Math.min((stats?.assignedLeads || 0) * 5, 100)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent Leads */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Recent Inquiries</h3>
              <button onClick={() => navigate('/dashboard/leads')} className="text-xs text-primary font-semibold hover:underline">View All</button>
            </div>
            {myLeadsList && myLeadsList.length > 0 ? (
              <div className="space-y-3">
                {myLeadsList.map((lead: any) => (
                  <div key={lead.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                    <div className={`h-2.5 w-2.5 rounded-full ${statusColors[lead.status] || 'bg-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{lead.name}</div>
                      <div className="text-xs text-muted-foreground">{lead.phone}</div>
                    </div>
                    <div className="text-[10px] text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No inquiries yet. List a property to start receiving leads.</p>
            )}
          </CardContent>
        </Card>

        {/* Agency Listings widget */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Agency Listings</h3>
              <button onClick={() => navigate('/dashboard/listings?scope=agency')} className="text-xs text-primary font-semibold hover:underline">View All</button>
            </div>
            {agencyListings && agencyListings.length > 0 ? (
              <div className="space-y-3">
                {agencyListings.map((prop: any) => (
                  <div key={prop.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Home className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {typeof prop.title === 'object' ? (prop.title as any)?.en || 'Property' : 'Property'}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {prop.city} · <Eye className="h-3 w-3" /> {prop.view_count || 0}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{prop.price?.toLocaleString()} {prop.currency || 'ETB'}</div>
                      {prop.user_id === user?.id && <Badge variant="outline" className="text-[9px] px-1 py-0">Mine</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{agency ? 'No listings from your agency yet.' : 'You are not linked to an agency.'}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, enabled, reason, onClick }: {
  icon: any; label: string; enabled: boolean; reason?: string; onClick: () => void;
}) {
  const btn = (
    <Button
      variant="outline" className="h-20 flex-col gap-1.5 w-full"
      disabled={!enabled}
      onClick={enabled ? onClick : undefined}
    >
      <Icon className="h-5 w-5 text-primary" /><span className="text-xs">{label}</span>
    </Button>
  );
  if (enabled || !reason) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="block">{btn}</span></TooltipTrigger>
      <TooltipContent><p className="text-xs max-w-[220px]">{reason}</p></TooltipContent>
    </Tooltip>
  );
}

